import TreeNode, {
  TreeNodeVariant,
  TreeNodeVariantStrings,
} from 'components/DesignSystems/OrganizationChart/TreeNode';
import { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import {
  Sections,
  commandStaffOrder,
  planningSectionOrder,
} from './incidentRosterChart.constants';
import { buildSeatBox, ResourceBox } from './IncidentRosterSeatBoxes';
import {
  Position,
  QuickAddHandler,
  ResourceHandlers,
  Section,
} from './incidentRosterChart.types';

export const checkIfVertical = (seatName: string) => {
  if (seatName.includes('Technical Specialist')) {
    return true;
  }

  switch (seatName) {
    case 'Compensation Unit Leader':
    case 'Cost Unit Leader':
    case 'Procurement Unit Leader':
    case 'Time Unit Leader':
    case 'Communications Unit Leader':
    case 'Food Unit Leader':
    case 'Medical Unit Leader':
    case 'Facilities Unit Leader':
    case 'Ground Support Unit Leader':
    case 'Supply Unit Leader':
    case 'Demobilization Unit Leader':
    case 'Documentation Unit Leader':
    case 'Resource Unit Leader':
    case 'Situation Unit Leader':
    case 'Investigative Operations Group Supervisor':
    case 'Intelligence Group Supervisor':
    case 'Technical Specialist':
    case 'Environmental Unit Leader':
    case 'Vessel Support Unit Leader':
      return true;
    default:
      return false;
  }
};

export const checkIfAssistant = (seatName: string) => {
  switch (seatName) {
    case 'Legal Officer':
    case 'Liaison Officer':
    case 'Safety Officer':
    case 'Public Information Officer':
    case 'Staging Area Manager':
      return true;
    default:
      return false;
  }
};

const getNodeVariant = (seatName: string): TreeNodeVariant => {
  if (checkIfAssistant(seatName)) {
    return TreeNodeVariantStrings.assistant;
  }

  if (checkIfVertical(seatName)) {
    return TreeNodeVariantStrings.vertical;
  }

  return TreeNodeVariantStrings.horizontal;
};

export const checkChildrenForMembers = ({
  parentSeat,
  incidentMembers,
  positions,
}: {
  parentSeat: string | null;
  incidentMembers: RosterEntry[];
  positions: Position[];
}): boolean => {
  const children = positions.filter((pos) => pos.parentSeat === parentSeat);

  for (const child of children) {
    const membersForSeat = incidentMembers.filter((member) =>
      member.incidentPositions?.some(
        (pos) => pos.positionId === child.positionId
      )
    );

    if (membersForSeat.length > 0) {
      return true;
    }

    if (
      checkChildrenForMembers({
        parentSeat: child.incidentSeat,
        incidentMembers,
        positions,
      })
    ) {
      return true;
    }
  }

  return false;
};

export const renderResourceNodes = (
  positionId: string,
  { resourcesByPosition, onEditResource }: ResourceHandlers
) => {
  const resources = resourcesByPosition.get(positionId) ?? [];
  return resources.map((resource, idx) => (
    <TreeNode
      key={`resource-${resource.id}`}
      label={<ResourceBox resource={resource} onEdit={onEditResource} />}
      variant={TreeNodeVariantStrings.horizontal}
      isLast={idx === resources.length - 1}
    />
  ));
};

export const buildTree = (
  parentSeat: string | null,
  incidentMembers: RosterEntry[],
  positions: Position[],
  showAssignedPositions: boolean,
  setShowEditPositionModal: (positionId: string) => void,
  resourceHandlers: ResourceHandlers,
  onQuickAdd: QuickAddHandler,
  currentSection: Section | null = null,
  depth: number = 0
) => {
  const children = positions.filter((pos) => pos.parentSeat === parentSeat);

  // If any children are "section roots", sort them by the order defined in `Sections`
  const sectionOrder = new Map([
    ...Sections.map((section, idx) => [section.sectionSeatName, idx] as const),
    ...commandStaffOrder.map(
      (seatName, idx) => [seatName, Sections.length + idx] as const
    ),
  ]);

  const hasSectionChildren = children.some((c) =>
    sectionOrder.has(c.incidentSeat)
  );

  const isPlanningSection = currentSection?.name === 'Planning Section';

  if (hasSectionChildren) {
    children.sort((a, b) => {
      const aIdx = sectionOrder.get(a.incidentSeat) ?? Number.POSITIVE_INFINITY;
      const bIdx = sectionOrder.get(b.incidentSeat) ?? Number.POSITIVE_INFINITY;

      if (aIdx !== bIdx) return aIdx - bIdx;

      // Non-section (or ties) fall back to alphabetical
      return a.incidentSeat.localeCompare(b.incidentSeat);
    });
  } else if (isPlanningSection) {
    children.sort((a, b) => {
      const aIdx =
        planningSectionOrder.findIndex((name) => name === a.incidentSeat) ??
        Number.POSITIVE_INFINITY;
      const bIdx =
        planningSectionOrder.findIndex((name) => name === b.incidentSeat) ??
        Number.POSITIVE_INFINITY;

      if (aIdx !== bIdx) return aIdx - bIdx;

      // Non-section (or ties) fall back to alphabetical
      return a.incidentSeat.localeCompare(b.incidentSeat);
    });
  } else {
    children.sort((a, b) => a.incidentSeat.localeCompare(b.incidentSeat));
  }

  const visibleChildren = children.filter((child) => {
    const membersForSeat = incidentMembers.filter((member) =>
      member.incidentPositions?.some(
        (pos) => pos.positionId === child.positionId
      )
    );

    if (membersForSeat.length === 0) {
      // Check if there are any children with members

      const hasChildWithMembers = checkChildrenForMembers({
        parentSeat: child.incidentSeat,
        incidentMembers,
        positions,
      });

      // Note: if we comment-out this early return, then we will show all the positions, regardless of assigned members.
      if (showAssignedPositions && !hasChildWithMembers) {
        return false; // Skip this node entirely
      }
    }

    return true;
  });

  const parentHasHorizontalChildren = visibleChildren.some(
    (child) =>
      getNodeVariant(child.incidentSeat) === TreeNodeVariantStrings.horizontal
  );

  return visibleChildren.map((child, index) => {
    const membersForSeat = incidentMembers.filter((member) =>
      member.incidentPositions?.some(
        (pos) => pos.positionId === child.positionId
      )
    );

    const sectionForChild: Section | undefined = Sections.find(
      (section) => section.sectionSeatName === child.incidentSeat
    );

    const variant = getNodeVariant(child.incidentSeat);
    const isLastSibling = index === visibleChildren.length - 1;
    const isLast =
      variant === TreeNodeVariantStrings.vertical && parentHasHorizontalChildren
        ? false
        : isLastSibling;

    if (child.incidentSeat) {
      return (
        <TreeNode
          key={child.incidentSeat}
          label={buildSeatBox({
            positionId: child.positionId,
            seatName: child.incidentSeat,
            members: membersForSeat,
            isWorkteam: child.isWorkgroup,
            setShowEditPositionModal,
            onQuickAdd,
            currentSection: sectionForChild || currentSection,
          })}
          variant={variant}
          isLast={isLast}
        >
          {buildTree(
            child.incidentSeat,
            incidentMembers,
            positions,
            showAssignedPositions,
            setShowEditPositionModal,
            resourceHandlers,
            onQuickAdd,
            sectionForChild || currentSection,
            depth + 1
          )}
          {renderResourceNodes(child.positionId, resourceHandlers)}
        </TreeNode>
      );
    }
  });
};
